import { useEffect, useRef, useCallback } from 'preact/hooks';
import { h } from 'preact';

/**
 * useExposure - 检测元素是否由不可见到可见
 * @param {Function} onExpose - 元素由不可见到可见时的回调函数
 * @param {Object} options - IntersectionObserver 配置选项
 * eg:
 * const options = {
    root: null, // 使用视口作为参照物
    rootMargin: '0px', // 不使用边缘
    threshold: 0.6 // 曝光比例阈值
  };
 * @returns {Object} ref - 传递给需要检测的 DOM 元素的 ref
 */
const useExposure = (onExpose: () => void, options = { threshold: 0.6 }) => {
  const elementRef = useRef(null);
  const hasBeenVisible = useRef(false); // 用于标记是否已经曝光过

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      // 检测元素由不可见到可见
      if (entry.isIntersecting && !hasBeenVisible.current) {
        hasBeenVisible.current = true; // 标记为已曝光
        onExpose();
      } else if (!entry.isIntersecting) {
        hasBeenVisible.current = false; // 元素重新变得不可见
      }
    },
    [onExpose]
  );

  useEffect(() => {
    if (elementRef.current) {
      const observer = new IntersectionObserver(handleIntersect, options);
      observer.observe(elementRef.current);

      return () => {
        if (elementRef.current) {
          observer.unobserve(elementRef.current);
        }
      };
    }
  }, [handleIntersect, options]);

  return elementRef;
};

function ExposeElement({
  elementTag = 'Image',
  src = '',
  children,
  handleExposure,
  className,
  imgStyle,
  ...restProps
}: {
  className: string;
  elementTag?: 'Image' | 'View';
  src?: string;
  children?: React.ReactNode;
  onClick?: (p: any) => void;
  handleExposure: () => void;
  style?: React.CSSProperties;
  imgStyle?: React.CSSProperties;
}) {
  const exposureImgRef = useExposure(() => {
    if (src) {
      handleExposure();
    }
  });

  if (elementTag === 'Image') {
    return (
      <div className={className} style={{ borderRadius: '8px' }}>
        {h('img', {
          ...restProps,
          ref: exposureImgRef,
          src: src ?? '',
          style: {
            display: 'block',
            width: '100%',
            height: 'auto',
            objectFit: 'cover',
            maxWidth: '100%',
            maxHeight: '100%',
            aspectRatio: '1 / 1',
            borderRadius: '8px',
            ...(imgStyle || {})
          }
        })}
      </div>
    );
  } else {
    return (
      <div {...restProps} ref={exposureImgRef} className={className}>
        {children}
      </div>
    );
  }
}

export { ExposeElement, useExposure };
